import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DemoModal from '../components/DemoModal';
import MediaUploadHero from '../components/MediaUploadHero';
import { Tv, Clock, Mic, FileText, Grid3x3, DollarSign, ArrowRight, Play, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Section: "Watch Our Latest Work"
// Fixed curated list of YouTube videos (no backend fetch). Thumbnails scroll
// continuously to the left in a seamless, hover-to-pause marquee. Clicking a
// thumbnail opens a centered popup player (youtube-nocookie embed, lazy
// loaded only on click) over a dimmed background.
// ---------------------------------------------------------------------------
const FIXED_VIDEO_IDS = [
  'wQhzcfjxFW0',
  'oLGRGsx7xoQ',
  '4sX0Tg24RE4',
  'tpWeH7XSxjU',
  'YTN9bVO4Q70',
  'FLbSzjQJqHo',
  'rnYUbEHVpJ4',
  'F3Knc7axRAQ',
  'oHtbccz9BX0',
  'ttD4hM8CrwQ',
  'hI6gxOUy8jc',
  'v876hPfLm8E',
];

const YouTubeLatestSection = () => {
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [activeVideoId, setActiveVideoId] = useState(null);

  const visibleIds = FIXED_VIDEO_IDS.filter((id) => !hiddenIds.has(id));
  // Duplicate once so the -50% translate marquee loops seamlessly.
  const marqueeIds = visibleIds.length > 0 ? [...visibleIds, ...visibleIds] : [];

  const handleThumbnailError = (id) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const openVideo = (id) => setActiveVideoId(id);
  const closeVideo = () => setActiveVideoId(null);

  // Close on Escape while the popup is open.
  useEffect(() => {
    if (!activeVideoId) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeVideo();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeVideoId]);

  return (
    <section className="min-h-[85vh] bg-white flex items-center justify-center" data-testid="youtube-latest-section">
      <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          className="text-center mb-8 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-medium text-black/90">
            Watch Our Latest Work
          </h2>
        </motion.div>

        {visibleIds.length > 0 && (
          <div className="relative overflow-hidden" data-testid="youtube-latest-carousel">
            <div className="flex gap-6 animate-scroll-left">
              {marqueeIds.map((id, idx) => (
                <button
                  key={`${id}-${idx}`}
                  type="button"
                  onClick={() => openVideo(id)}
                  className="group flex-shrink-0 w-[320px] rounded-xl overflow-hidden border border-[#D4DDD1]/40 bg-[#F5F7F5] hover:border-[#E8A0B8]/60 hover:scale-[1.04] hover:shadow-lg transition-transform duration-300 ease-out cursor-pointer"
                  data-testid="youtube-video-card"
                  aria-label="Play video"
                >
                  <div className="relative aspect-video bg-[#F5F7F5] overflow-hidden rounded-xl">
                    <img
                      src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                      alt="Latest work video thumbnail"
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={() => handleThumbnailError(id)}
                      data-testid={`youtube-thumbnail-${id}`}
                    />
                    {/* Subtle play icon overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/25 transition-colors duration-300">
                      <div className="w-14 h-14 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center shadow-md group-hover:bg-white transition-colors duration-300">
                        <Play className="w-6 h-6 text-[#E8A0B8] fill-[#E8A0B8] ml-0.5" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeVideoId && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeVideo}
          data-testid="youtube-video-modal-overlay"
        >
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeVideo}
              className="absolute -top-11 right-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors duration-200"
              aria-label="Close video"
              data-testid="youtube-video-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl bg-black">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                title="YouTube video player"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="youtube-video-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const ProductB = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  const features = [
    { 
      icon: Tv,
      title: 'Big-Screen Quality', 
      description: 'Broadcast-ready production value' 
    },
    { 
      icon: Clock,
      title: 'Fast Turnaround', 
      description: 'From concept to air in days, not months' 
    },
    { 
      icon: Mic,
      title: 'Professional Voiceover', 
      description: 'Studio-quality narration' 
    },
    { 
      icon: FileText,
      title: 'Custom Scripting', 
      description: 'Tailored messaging for your brand' 
    },
    { 
      icon: Grid3x3,
      title: 'Multiple Formats', 
      description: 'TV, streaming, digital — all formats' 
    },
    { 
      icon: DollarSign,
      title: 'Affordable Pricing', 
      description: 'Enterprise quality without the cost' 
    },
  ];

  return (
    <div className="w-full">
      {/* Interactive Media Upload Hero */}
      <MediaUploadHero
        title="Broadcast Ready Commercials"
        description="Big-Screen Ads for Growing Businesses. Professional commercials that compete with the big brands."
        ctaText="Get started"
        onCtaClick={() => setDemoOpen(true)}
        initialMedia="https://customer-q87c1i5e2o40bvjt.cloudflarestream.com/bbc0a2f1585878d5fd228db32ea3b96d/downloads/default.mp4"
        initialMediaType="video"
      />

      {/* Features Section - min-h-[85vh] */}
      <section className="min-h-[85vh] bg-white flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4">
              Everything you need for broadcast-quality commercials
            </h2>
            <p className="text-lg text-black/70 max-w-2xl mx-auto">
              Professional production without the professional price tag
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full bg-[#E8E4DC] border-[#F4C2D4]/30 hover:bg-[#FDF7F9] hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#E8A0B8]" />
                    </div>
                    <CardTitle className="text-black/90 text-lg font-sans">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Content Section - min-h-[85vh] */}
      <section className="min-h-[85vh] bg-[#F5F7F5] flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4">
                Trusted by Growing Brands
              </h2>
              <p className="text-lg text-black/70 max-w-2xl mx-auto">
                Join hundreds of businesses creating broadcast-quality commercials without the agency overhead.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-white border-[#D4DDD1]/40">
                <CardHeader>
                  <CardTitle className="text-xl font-medium text-black/90 mb-2">
                    Creative Excellence
                  </CardTitle>
                  <CardDescription className="text-black/70">
                    Award-winning creative teams bring your vision to life with cinematic storytelling and brand-perfect messaging.
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="bg-white border-[#D4DDD1]/40">
                <CardHeader>
                  <CardTitle className="text-xl font-medium text-black/90 mb-2">
                    End-to-End Production
                  </CardTitle>
                  <CardDescription className="text-black/70">
                    From concept to final cut, we handle every aspect of production with professional precision.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4 — Watch Our Latest Work (dynamic YouTube feed) */}
      <YouTubeLatestSection />

      {/* CTA */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#E8EDE7] to-[#D4DDD1]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/95 mb-6">
              Ready to get started?
            </h2>
            <p className="text-lg text-black/70 mb-8 max-w-2xl mx-auto">
              Experience elegant intelligence. Book a demo today.
            </p>
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
            >
              Book a demo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default ProductB;
